import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { notification } from "antd";

import { useLazyDownloadSurveyViewQuery } from "@/redux/service";
import { RootState } from "@/redux/store";
import { clearDownload, setDownloadState } from "@/redux/downloads/reducer";
import { NotificationDescription } from "@/components_v2/Notification/NotificationDescription";
import { downloadPollingService } from "@/shared/services/downloadPollingService";
import { UseSurveyViewDownloadOptions } from "@/domains/survey-views/types";

export const useSurveyViewDownload = (
  options: UseSurveyViewDownloadOptions = {},
) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [downloadSurveyViewQuery] = useLazyDownloadSurveyViewQuery();

  const downloadingStates = useSelector(
    (state: RootState) => state.downloads.downloadingStates,
  );

  const { pollInterval = 2000, timeout = 300000 } = options;

  const showSuccessNotification = useCallback(
    (link: string) => {
      notification.success({
        message: (
          <div>
            <p>{t("surveyViews.labels.reportReady")}</p>
            <p>
              {t("surveyViews.labels.downloadDoesntStartAutomatically")},{" "}
              <a href={link} target="_blank" download>
                {t("surveyViews.labels.clickHere")}
              </a>{" "}
              {t("surveyViews.labels.toDownload")}
            </p>
          </div>
        ),
        placement: "bottomRight",
        duration: 30,
      });
    },
    [t],
  );

  const showErrorNotification = useCallback(() => {
    notification.error({
      message: t("surveyViews.labels.downloadFailed"),
      placement: "bottomRight",
    });
  }, [t]);

  const showTimeoutNotification = useCallback(() => {
    notification.warning({
      message: t("surveyViews.labels.downloadTimeout"),
      placement: "bottomRight",
    });
  }, [t]);

  const handleCleanup = useCallback(
    (surveyViewId: string) => {
      dispatch(clearDownload(surveyViewId));
    },
    [dispatch],
  );

  const createNotificationDescription = (
    filename: string,
    progress?: number,
  ) => (
    <NotificationDescription
      filename={filename}
      descriptionKey="surveyViews.labels.generatingReportDescription"
      progress={progress}
    />
  );

  const downloadSurveyView = useCallback(
    async (surveyViewId: string, filename: string = "Report.pdf") => {
      if (downloadingStates[surveyViewId]) {
        return;
      }

      dispatch(setDownloadState({ id: surveyViewId, isDownloading: true }));

      await downloadPollingService.initiateDownload(
        surveyViewId,
        downloadSurveyViewQuery,
        { pollInterval, timeout },
        {
          onSuccess: (response) => {
            dispatch(
              setDownloadState({ id: surveyViewId, isDownloading: false }),
            );
            showSuccessNotification(response.link);
          },
          onError: () => {
            dispatch(
              setDownloadState({ id: surveyViewId, isDownloading: false }),
            );
            showErrorNotification();
          },
          onTimeout: () => {
            showTimeoutNotification();
          },
          onCleanup: handleCleanup,
        },
        (progress?: number) => ({
          message: t("surveyViews.labels.generatingReportTitle"),
          description: createNotificationDescription(filename, progress),
        }),
      );
    },
    [
      downloadingStates,
      downloadSurveyViewQuery,
      dispatch,
      t,
      pollInterval,
      timeout,
      showSuccessNotification,
      showErrorNotification,
      showTimeoutNotification,
      handleCleanup,
    ],
  );

  const isDownloading = useCallback(
    (surveyViewId: string) => downloadingStates[surveyViewId] || false,
    [downloadingStates],
  );

  return {
    downloadSurveyView,
    isDownloading,
  };
};
